import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface VacuumsViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
  database?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${
              isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
            }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[9px] truncate max-w-[130px] font-mono" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number'
                ? entry.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                : (entry.value == null ? 'Data not collected' : entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const VacuumsView: React.FC<VacuumsViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
  database,
}) => {
  const [loading, setLoading] = useState(true);
  const [vacuumData, setVacuumData] = useState<any>(null);

  const isMySQL = ['mysql', 'mariadb'].includes((dbType || '').toLowerCase());
  const isMariaDB = (dbType || '').toLowerCase() === 'mariadb';

  // Legend Hover States
  const [hoveredLive, setHoveredLive] = useState<string | null>(null);
  const [hoveredDead, setHoveredDead] = useState<string | null>(null);
  const [hoveredAutovac, setHoveredAutovac] = useState<string | null>(null);
  const [hoveredAutoanalyze, setHoveredAutoanalyze] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '15');

    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;
    if (database) params.database = database;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/per_db/vacuum', { params });
      setVacuumData(res.data?.data || res.data);
    } catch (err) {
      console.warn('VacuumsView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, database]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const formatSeries = (labels: string[], databases: string[], seriesMap: any, metricKey: string) => {
    if (!labels || labels.length === 0 || !databases || databases.length === 0) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: any = { time: timeLabels[idx] };
      databases.forEach((db) => {
        const val = seriesMap?.[`${db}:${metricKey}`]?.[idx];
        point[db] = val !== undefined && val !== null ? Number(val.toFixed(2)) : null;
      });
      return point;
    });
  };

  const liveFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'live_tuples');
  const deadFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'dead_tuples');
  const autovacAgeFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'last_autovacuum_age_sec');
  const autoanalyzeAgeFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'last_autoanalyze_age_sec');

  const colors = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#a855f7', // Purple
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#06b6d4', // Cyan
    '#ec4899', // Pink
    '#84cc16', // Lime
    '#6366f1', // Indigo
    '#f97316', // Orange
    '#14b8a6', // Teal
    '#eab308', // Yellow
    '#8b5cf6', // Violet
    '#22c55e', // Emerald
    '#e11d48', // Rose
  ];
  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) =>
      Object.keys(item).some((k) => k !== 'time' && item[k] !== null && item[k] !== undefined)
    );
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[300px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }
  return (
    <div className="space-y-3 relative">
      <div>
        <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <div>
            <Sparkles className="w-4 h-4 text-orange-500" />
          </div>
        {
          isMariaDB ? (
            <>
              Table Maintenance
            </>
          ) : (
            <>
              Vacuum & Table Maintenance Diagnostics
            </>
          )
        }
        </h2>
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div>
          <div className="mb-3 border-b border-gray-100 dark:border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
              {isMariaDB ? "Estimated rows per database" : "Live Rows per DB"}</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(liveFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={liveFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                  <XAxis dataKey="time" stroke="#6b7280" fontSize={10} tickLine={false} />
                  <YAxis stroke="#6b7280" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredLive} onHoverSeries={setHoveredLive} />} />
                  {Object.keys(liveFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                    const color = colors[i % colors.length];
                    return (
                      <Line
                        key={dbKey}
                        type="monotone"
                        dataKey={dbKey}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredLive, dbKey)}
                        strokeWidth={hoveredLive === dbKey ? 2.5 : 1.5}
                        dot={getDot(hoveredLive, dbKey, color)}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">Data not collected</span>
            )}
          </div>
        </div>

        <div>
          <div className="mb-3 border-b border-gray-100 dark:border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Dead Rows per DB</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(deadFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={deadFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                  <XAxis dataKey="time" stroke="#6b7280" fontSize={10} tickLine={false} />
                  <YAxis stroke="#6b7280" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredDead} onHoverSeries={setHoveredDead} />} />
                  {Object.keys(deadFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                    const color = colors[(i + 3) % colors.length];
                    return (
                      <Line
                        key={dbKey}
                        type="monotone"
                        dataKey={dbKey}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredDead, dbKey)}
                        strokeWidth={hoveredDead === dbKey ? 2.5 : 1.5}
                        dot={getDot(hoveredDead, dbKey, color)}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">Data not collected</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 2 - Oldest Autovacuum and Autoanalyze Age (PostgreSQL / Non-MySQL) */}
      {!isMySQL && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
          <div>
            <div className="mb-3 border-b border-gray-100 dark:border-gray-700/50 pb-2">
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Oldest Autovacuum Age by DB (seconds)</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(autovacAgeFormatted) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={autovacAgeFormatted}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={10} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={10} tickLine={false} tickCount={3} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredAutovac} onHoverSeries={setHoveredAutovac} />} />
                    {Object.keys(autovacAgeFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                      const color = colors[i % colors.length];
                      return (
                        <Line
                          key={dbKey}
                          type="monotone"
                          dataKey={dbKey}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredAutovac, dbKey)}
                          strokeWidth={hoveredAutovac === dbKey ? 2.5 : 1.5}
                          dot={getDot(hoveredAutovac, dbKey, color)}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-400 dark:text-gray-500 italic">Data not collected</span>
              )}
            </div>
          </div>

          <div>
            <div className="mb-3 border-b border-gray-100 dark:border-gray-700/50 pb-2">
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Oldest Autoanalyze Age by DB (seconds)</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(autoanalyzeAgeFormatted) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={autoanalyzeAgeFormatted}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={10} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={10} tickLine={false} tickCount={3} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredAutoanalyze} onHoverSeries={setHoveredAutoanalyze} />} />
                    {Object.keys(autoanalyzeAgeFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                      const color = colors[(i + 2) % colors.length];
                      return (
                        <Line
                          key={dbKey}
                          type="monotone"
                          dataKey={dbKey}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredAutoanalyze, dbKey)}
                          strokeWidth={hoveredAutoanalyze === dbKey ? 2.5 : 1.5}
                          dot={getDot(hoveredAutoanalyze, dbKey, color)}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-400 dark:text-gray-500 italic">Data not collected</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

